import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IReservationData, IRoomOccupancy, ISearchParams } from '../interfaces';
import { formatDate } from '@angular/common';
import { SettingsService } from './settings.service';
import { DebugService } from './debug.service';
import { ConsentService, IConsentState } from './consent.service';
import { de } from 'intl-tel-input/i18n';

declare var createAnalytics: (analyticsTrackingId: string | string[]) => void;
declare var createMetaPixel: (metaPixelId: string | string[]) => void;
declare var createLastbotScript: (config: any) => void;

export interface ITableReservationAnalytics {
  reservationId: string;
  hotelCode: string;
  eventSiteId: string;
  eventSiteName: string;
  eventId: string;
  eventName: string;
  guestCount: number;
  /** Hotel date, yyyy-MM-dd */
  reservationDate: string;
  /** HH:mm */
  startTime: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private ga4Id: string | string[] | null = null;
  private metaPixelId: string | string[] | null = null;
  private gaStarted = false;
  private metaStarted = false;

  // Events are sent only when the visitor has consented and the tool is configured
  private gaEnabled = false;
  private metaPixelEnabled = false;

  constructor(
    private router: Router,
    private settings: SettingsService,
    private debug: DebugService,
    private consent: ConsentService
  ) { }

  onAppInit() {

    return new Promise<boolean>((resolve , reject) => {
      let GA4ID = this.settings.get('app.googleAnalytics4id');
      let metaPixelID = this.settings.get('app.metaPixelId');
      let lastBotConfig = this.settings.get('app.lastBotConfiguration');
      this.debug.log('AnalyticsService', 'onAppInit', 'GA4ID:', GA4ID);
      this.debug.log('AnalyticsService', 'onAppInit', 'metaPixelID:', metaPixelID);
      this.debug.log('AnalyticsService', 'onAppInit', 'lastBotConfig:', lastBotConfig);

      this.ga4Id = GA4ID || null;
      this.metaPixelId = metaPixelID || null;

      // GA4 and Meta Pixel scripts are loaded only after the visitor has consented
      this.consent.init(!!this.ga4Id || !!this.metaPixelId, this.settings.askForCookieConsent());
      this.consent.state$.subscribe(state => this.applyConsent(state));

      if (lastBotConfig && lastBotConfig.widgetId) {
        createLastbotScript(lastBotConfig);
      }
      resolve(true);
    });
  }

  private applyConsent(state: IConsentState) {
    const analyticsAllowed = state.analytics && !!this.ga4Id;
    const marketingAllowed = state.marketing && !!this.metaPixelId;

    if (analyticsAllowed && !this.gaStarted) {
      createAnalytics(this.ga4Id!);
      this.gaStarted = true;
    } else if (this.gaStarted && analyticsAllowed !== this.gaEnabled) {
      this.callGtag('consent', 'update', { analytics_storage: analyticsAllowed ? 'granted' : 'denied' });
    }
    if (this.gaStarted && !analyticsAllowed) {
      this.deleteCookies(name => name === '_ga' || name.startsWith('_ga_') || name === '_gid' || name.startsWith('_gat'));
    }
    this.gaEnabled = analyticsAllowed;

    if (marketingAllowed && !this.metaStarted) {
      createMetaPixel(this.metaPixelId!);
      this.metaStarted = true;
    } else if (this.metaStarted && marketingAllowed !== this.metaPixelEnabled) {
      this.callFbq('consent', marketingAllowed ? 'grant' : 'revoke');
    }
    if (this.metaStarted && !marketingAllowed) {
      this.deleteCookies(name => name === '_fbp' || name === '_fbc');
    }
    this.metaPixelEnabled = marketingAllowed;
  }

  private callGtag(...args: any[]) {
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') {
      gtag(...args);
    }
  }

  private callFbq(...args: any[]) {
    const fbq = (window as any).fbq;
    if (typeof fbq === 'function') {
      fbq(...args);
    }
  }

  /** Deletes cookies from the current host and its parent domains (e.g. book.customer.fi and customer.fi) */
  private deleteCookies(isTarget: (name: string) => boolean) {
    const hostParts = window.location.hostname.split('.');
    const domains: (string | null)[] = [null];
    for (let i = 0; i < hostParts.length - 1; i++) {
      domains.push(hostParts.slice(i).join('.'));
    }

    document.cookie
      .split(';')
      .map(cookie => cookie.split('=')[0].trim())
      .filter(isTarget)
      .forEach(name => domains.forEach(domain => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
      }));
  }

  /** Sends a GA4 event if the visitor has consented to analytics. Never breaks the booking or payment flow. */
  private trackGa(eventName: string, params: Record<string, any>) {
    try {
      if (this.gaEnabled) {
        this.callGtag('event', eventName, params);
      }
    } catch (error) {
      this.debug.log('AnalyticsService', 'trackGa', 'failed', eventName, error);
    }
  }

  /** Sends a Meta Pixel standard event if the visitor has consented to marketing. Never breaks the booking or payment flow. */
  private trackMeta(eventName: string, buildParams: () => Record<string, any>) {
    try {
      const fbq = (window as any).fbq;
      if (!this.metaPixelEnabled || typeof fbq !== 'function') {
        return;
      }

      fbq('track', eventName, buildParams());
    } catch (error) {
      this.debug.log('AnalyticsService', 'trackMeta', 'failed', eventName, error);
    }
  }

  /** Converts GA4 items to Meta Pixel content_ids / contents / num_items parameters */
  private metaContentParams(items: any[]) {
    return {
      content_type: 'product',
      content_ids: items.map(i => i.item_id).filter(id => !!id),
      contents: items.map(i => ({ id: i.item_id, quantity: i.quantity, item_price: i.price })),
      num_items: items.reduce((sum, i) => sum + (i.quantity || 0), 0)
    };
  }

  bookingEngineMakeSearch(searchParams: ISearchParams) {

    let totalAdults = 0;
    let totalChildren = 0;
    searchParams.roomData.forEach((room:IRoomOccupancy) => {
      totalAdults += room.adults;
      totalChildren += room.children;
    });

    this.trackGa("make_search", {
      arrival: formatDate(searchParams.arrival, 'yyyy-MM-dd', 'fi'),
      departure: formatDate(searchParams.departure, 'yyyy-MM-dd', 'fi'),
      rooms: searchParams.roomData.length,
      adults: totalAdults,
      children: totalChildren,
      booking_code: searchParams.bookingCode,
      category: searchParams.category,
      hotel_code: searchParams.hotelCode
    });

    this.trackMeta('Search', () => ({
      checkin_date: formatDate(searchParams.arrival, 'yyyy-MM-dd', 'fi'),
      checkout_date: formatDate(searchParams.departure, 'yyyy-MM-dd', 'fi'),
      num_adults: totalAdults,
      num_children: totalChildren,
      content_category: searchParams.hotelCode,
      // booking code entered in the search, if any
      ...(searchParams.bookingCode ? { search_string: searchParams.bookingCode } : {})
    }));
  }

  bookingEngineViewItemList(availabilityResponseObj:any, searchParams:ISearchParams) {
    availabilityResponseObj.forEach((roomObj:any, index:number) => {
    
      let finalItems:any = [];

      roomObj.forEach((room:any) => {
        
        let items = room.roomrates.map((roomRate:any, index:number) => {
          let discount = roomRate.totalOriginalPrice - roomRate.totalPriceForStayWithTaxes;

          return {
            item_id: roomRate.room?.roomCode,
            item_name: roomRate.room?.pim?.name || roomRate.room?.roomName,
            affiliation: searchParams.hotelCode,
            coupon: searchParams.bookingCode,
            index: index,
            discount: discount,
            item_category: roomRate.room?.roomCategory,
            price: roomRate.totalPriceForStayWithTaxes,
            quantity: 1,
            item_variant: roomRate.rate?.pim?.rateName || roomRate.rate.rateName,
            // item scoped custom parameters. Need to be configured in analytics to appear in reports
            // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
            rate_code: roomRate.rate.rateCode,
            room_name_pms: roomRate.room?.roomNamePms,
            rate_category: roomRate.rate?.rateCategory,
            cancel_policy_code: roomRate.rate?.cancelPolicyCode,
            mandatory_deposit_type: roomRate.rate?.mandatoryDepositType
          }
        });

        finalItems.push(...items);
      });

      this.trackGa("view_item_list", {
        item_list_id: `availability_list_room_${index+1}`,
        item_list_name: `Availability list for room ${index+1}`,
        items: finalItems
      });

      this.trackMeta('ViewContent', () => ({
        ...this.metaContentParams(finalItems),
        content_name: `Availability list for room ${index+1}`,
        content_category: searchParams.hotelCode
      }));
    });
    
    
  }

  bookingEngineAddToCart(roomRate: any, searchParams: ISearchParams) {
    
    let discount = roomRate.totalOriginalPrice - roomRate.totalPriceForStayWithTaxes;
    
    let i:Record<string, string|number> = {
        item_id: roomRate.room?.roomCode,
        item_name: roomRate.room?.pim?.name || roomRate.room?.roomName,
        affiliation: searchParams.hotelCode,
        coupon: searchParams.bookingCode,
        discount: discount,
        item_category: roomRate.room?.roomCategory,
        price: roomRate.totalPriceForStayWithTaxes,
        quantity: 1,
        item_variant: roomRate.rate?.pim?.rateName || roomRate.rate.rateName,
        // item scoped custom parameters. Need to be configured in analytics to appear in reports
        // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
        rate_code: roomRate.rate.rateCode,
        room_name_pms: roomRate.room?.roomNamePms,
        rate_category: roomRate.rate?.rateCategory,
        cancel_policy_code: roomRate.rate?.cancelPolicyCode,
        mandatory_deposit_type: roomRate.rate?.mandatoryDepositType

    };

    this.trackGa("add_to_cart", {
        currency: roomRate.currency,
        value: roomRate.totalPriceForStayWithTaxes,
        items: [
          i
        ]
      });

    this.trackMeta('AddToCart', () => ({
      ...this.metaContentParams([i]),
      currency: roomRate.currency,
      value: roomRate.totalPriceForStayWithTaxes
    }));
  }

  bookingEngineBeginCheckout(reservationItems:any) {
    // fired when payment is started
    let totalValue = 0;
    let items:any = [];
    reservationItems.forEach((room:any, index:number) => {
      totalValue += room.TotalPriceForStayWithTaxes;

      let roomRate = room.RoomRate ?? room.roomRate;
      let searchParams = room.SearchMask ?? room.searchMask;
      let discount = roomRate.totalOriginalPrice - roomRate.totalPriceForStayWithTaxes;


      items.push({
        item_id: roomRate.room?.roomCode,
        item_name: roomRate.room?.pim?.name || roomRate.room?.roomName,
        affiliation: searchParams.HotelCode,
        coupon: searchParams.BookingCode,
        discount: discount,
        index: index,
        item_category: roomRate.room?.roomCategory,
        quantity: 1,
        item_variant: roomRate.rate?.pim?.rateName || roomRate.rate.rateName,
        price: roomRate.totalPriceForStayWithTaxes,
         // item scoped custom parameters. Need to be configured in analytics to appear in reports
        // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
        rate_code: roomRate.rate.rateCode,
        room_name_pms: roomRate.room?.roomNamePms,
        rate_category: roomRate.rate?.rateCategory,
        cancel_policy_code: roomRate.rate?.cancelPolicyCode,
        mandatory_deposit_type: roomRate.rate?.mandatoryDepositType
      });
    });

    this.trackGa("begin_checkout", {
      currency: reservationItems[0].RoomRate.currency,
      value: totalValue,
      coupon: reservationItems[0].SearchMask.BookingCode,
      items: items
    });

    this.trackMeta('InitiateCheckout', () => ({
      ...this.metaContentParams(items),
      currency: reservationItems[0].RoomRate.currency,
      value: totalValue
    }));
  }

  bookingEnginePurchase(reservationItems:any, transactionId:string) {
    // fired when payment is succesful
    let totalValue = 0;
    let items:any = [];
    let currency = 'EUR';
    let coupon = '';
    reservationItems.forEach((room:any, index:number) => {
      totalValue += room.TotalPriceForStayWithTaxes ?? room.totalPriceForStayWithTaxes;

      let roomRate = room.RoomRate ?? room.roomRate;
      let searchParams = room.SearchMask ?? room.searchMask;
      currency =  roomRate.Currency || roomRate.currency || 'EUR';
      coupon = searchParams.BookingCode || searchParams.bookingCode || '';


      let discount = 0;
      
      if (roomRate.totalOriginalPrice) {
        discount = roomRate.totalOriginalPrice - roomRate.totalPriceForStayWithTaxes;
      }


      items.push({
        item_id: roomRate.room?.roomCode,
        item_name: roomRate.room?.pim?.name || roomRate.room?.roomName,
        affiliation: searchParams.HotelCode,
        coupon: searchParams.BookingCode || searchParams.bookingCode,
        discount: discount,
        index: index,
        item_category: roomRate.room?.roomCategory,
        quantity: 1,
        item_variant: roomRate.rate?.pim?.rateName || roomRate.rate.rateName,
        price: roomRate.totalPriceForStayWithTaxes,
         // item scoped custom parameters. Need to be configured in analytics to appear in reports
        // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
        rate_code: roomRate.rate.rateCode,
        room_name_pms: roomRate.room?.roomNamePms,
        rate_category: roomRate.rate?.rateCategory,
        cancel_policy_code: roomRate.rate?.cancelPolicyCode,
        mandatory_deposit_type: roomRate.rate?.mandatoryDepositType
      });
    });

    this.trackGa("purchase", {
        transaction_id: transactionId,
        value: totalValue,
        currency: currency,
        coupon: coupon,
        items: items
    });

    this.trackMeta('Purchase', () => ({
      ...this.metaContentParams(items),
      currency: currency,
      value: totalValue
    }));
  }

  giftCardViewItemList(giftCardData:any[]) {
    let items = giftCardData.map((gcProduct:any, index:number) => {

      return {
          item_id: gcProduct.id,
          item_name: gcProduct.pim?.name || gcProduct.title,
          affiliation: this.settings.get('app.endpoints')?.[0]?.hotelCode || 'unknown',
          coupon: null,
          index: index,
          discount: null,
          item_category: null,
          price: gcProduct.pricing?.price || gcProduct.pricing?.minPrice || 0,
          quantity: 1,
          item_variant: null,
          // item scoped custom parameters. Need to be configured in analytics to appear in reports
          // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
          price_type: gcProduct.pricing?.priceType || null,

      }
    });

    this.trackGa("view_item_list", {
      item_list_id: `giftcard_list`,
      item_list_name: `Gift Card List`,
      items: items
    });

    this.trackMeta('ViewContent', () => ({
      ...this.metaContentParams(items),
      content_name: 'Gift Card List'
    }));
  }

  giftCardAddToCart(basketItem: any) {
    let i:Record<string, string|number|null> = {
        item_id: basketItem.giftCard.id,
        item_name: basketItem.giftCard.pim?.name || basketItem.giftCard.title,
        affiliation: this.settings.get('app.endpoints')?.[0]?.hotelCode || 'unknown',
        coupon: null,
        discount: null,
        item_category: basketItem.giftCard.category || null,
        price: basketItem.unitPrice,
        quantity: basketItem.quantity,
        item_variant: null,
        item_list_id: `giftcard_list`,
        item_list_name: `Gift Card List`,
        // item scoped custom parameters. Need to be configured in analytics to appear in reports
        // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
        price_type: basketItem.giftCard.pricing?.priceType || null,

    };

    this.trackGa("add_to_cart", {
        currency: 'EUR',
        value: basketItem.sumValue,
        items: [
          i
        ]
      });

    this.trackMeta('AddToCart', () => ({
      ...this.metaContentParams([i]),
      currency: 'EUR',
      value: basketItem.sumValue
    }));
  }

  giftCardRemoveFromCart(basketItem: any) {
    let i:Record<string, string|number|null> = {
        item_id: basketItem.giftCard.id,
        item_name: basketItem.giftCard.pim?.name || basketItem.giftCard.title,
        affiliation: this.settings.get('app.endpoints')?.[0]?.hotelCode || 'unknown',
        coupon: null,
        discount: null,
        item_category: basketItem.giftCard.category || null,
        price: basketItem.unitPrice,
        quantity: basketItem.quantity,
        item_variant: null,
        item_list_id: `giftcard_list`,
        item_list_name: `Gift Card List`,
        // item scoped custom parameters. Need to be configured in analytics to appear in reports
        // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
        price_type: basketItem.giftCard.pricing?.priceType || null,

    };

    this.trackGa("remove_from_cart", {
        currency: 'EUR',
        value: basketItem.sumValue,
        items: [
          i
        ]
      });
  }

  giftCardBeginCheckout(basket:any[]) {
    // fired when payment is started
    let totalValue = 0;
    let items:any = [];
    basket.forEach((basketItem:any, index:number) => {
      totalValue += basketItem.sumValue;

      items.push({
        item_id: basketItem.giftCard.id,
        item_name: basketItem.giftCard.pim?.name || basketItem.giftCard.title,
        affiliation: this.settings.get('app.endpoints')?.[0]?.hotelCode || 'unknown',
        coupon: null,
        discount: null,
        index: index,
        item_category: null,
        quantity: basketItem.quantity,
        item_variant: null,
        price: basketItem.unitPrice,
         // item scoped custom parameters. Need to be configured in analytics to appear in reports
        // ohje https://support.google.com/analytics/answer/14239695?hl=en&ref_topic=11151952&sjid=3294491751373711200-EU#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
        price_type: basketItem.giftCard.pricing?.priceType || null,

      });
    });

    this.trackGa("begin_checkout", {
      currency: 'EUR',
      value: totalValue,
      coupon: null,
      items: items
    });

    this.trackMeta('InitiateCheckout', () => ({
      ...this.metaContentParams(items),
      currency: 'EUR',
      value: totalValue
    }));
  }

  giftCardPurchase(order: any, transactionId: string) {

    // Normalize: accept array OR single object
    const orders = Array.isArray(order) ? order : (order ? [order] : []);

    let totalValue = 0;
    const items: any[] = [];

    orders.forEach((gc: any) => {
      const giftCards = Array.isArray(gc?.giftCards) ? gc.giftCards : [];

      giftCards.forEach((item: any, index: number) => {
        // "20,50 €" -> 20.50
        const numericValue =
          parseFloat(
            String(item?.value ?? '')
              .replace(/[€$\s]/g, '')   // remove currency + spaces
              .replace(',', '.')        // decimal comma -> dot
          ) || 0;

        totalValue += numericValue;

        items.push({
          item_id: item.productId,
          item_name: item.pim?.name || item.title,
          affiliation: this.settings.get('app.endpoints')?.[0]?.hotelCode || 'unknown',
          coupon: null,
          discount: null,
          index,
          item_category: null,
          quantity: 1,
          item_variant: null,
          price: numericValue,
        });
      });
    });

    this.trackGa("purchase", {
      transaction_id: transactionId,
      value: totalValue,
      currency: 'EUR',
      coupon: null,
      items,
    });

    this.trackMeta('Purchase', () => ({
      ...this.metaContentParams(items),
      currency: 'EUR',
      value: totalValue
    }));
  }

  /**
   * Fired when a table reservation has been made successfully.
   * Custom event (not ecommerce), because table reservation has no payment.
   * Mark as a key event in GA4. Custom parameters need to be registered as
   * custom dimensions in GA4 to appear in reports.
   */
  tableReservationComplete(params: ITableReservationAnalytics) {
    this.trackGa("table_reservation_complete", {
      transaction_id: params.reservationId,
      hotel_code: params.hotelCode,
      event_site_id: params.eventSiteId,
      event_site_name: params.eventSiteName,
      event_id: params.eventId,
      event_name: params.eventName,
      guests: params.guestCount,
      reservation_date: params.reservationDate,
      start_time: params.startTime
    });

    // Meta standard event "Schedule" = a booking was made. No value, since there is no payment.
    // Reservation id is not sent to Meta.
    this.trackMeta('Schedule', () => ({
      content_name: params.eventName,
      content_category: params.hotelCode,
      content_ids: [params.eventSiteId],
      num_items: params.guestCount
    }));
  }




}
